import { useEffect, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { Dialog } from '../../components/Dialog';
import { IssuedInvitationDialog } from './People';
import type { IssuedInvitation } from '../../services/identityPlatform';
import {
  OEM_PROFILE_FIELDS,
  createOEMPartner,
  createOEMUser,
  grantOEMAdmin,
  issueActivation,
  issuePasswordReset,
  deleteOEMPartner,
  deleteOEMUser,
  previewOEMPartnerDelete,
  type OEMDeleteImpact,
  provisionManagedAccount,
  resetMFA,
  listOEMCustomers,
  listOEMPartners,
  listOEMRoleAssignments,
  listOEMUsers,
  revokeOEMAdmin,
  setOEMPartnerEnabled,
  updateOEMPartner,
  type ActivationLink,
  type ManagedAccountStatus,
  type OEMCustomer,
  type OEMPartner,
  type OEMProfile,
  type OEMRoleAssignment,
  type OEMUser,
} from '../../services/oemAdmin';
import { ACCOUNT_STATE_LABEL, IDENTITY_NOTE, STATUS_LABEL, activationAction, describeMFAReset, loginEmailError, recoveryActions, changedProfile, describeOEMStatusChange, errorText, formatDate, validateOEMProfile } from '../oem/oemModel';
import { deletedNotice, describeOEMDelete, describeOEMUserDelete } from './lifecycleModel';

// Settings -> OEM Partners (platform administrators; documentation/oem-partner-architecture.md
// section D). OEM Partners are a delegated-administration layer above customers: they never own
// networks, radios or operational data.

const input = 'bg-gray-900 border border-gray-600 rounded px-3 py-1.5 text-sm w-full';
const btn = 'px-3 py-1.5 rounded text-sm font-medium disabled:opacity-40';


export function StatusBadge({ status }: { status: 'active' | 'disabled' }) {
  return (
    <span className={`inline-block text-xs font-semibold px-2 py-0.5 rounded border ${status === 'active' ? 'border-green-600 text-green-300 bg-green-900/30' : 'border-gray-500 text-gray-300 bg-gray-700'}`}>
      {status === 'active' ? '● ' : '○ '}{STATUS_LABEL[status]}
    </span>
  );
}

// The modal lives in components/Dialog (re-exported here for existing imports).
export { Dialog };

export function OEMPartnersTable({ partners, selected, onSelect }: { partners: OEMPartner[]; selected: string; onSelect: (id: string) => void }) {
  if (partners.length === 0) return <p className="text-sm text-gray-400">No OEM Partners yet. Every customer is a direct RadioOS customer.</p>;
  return (
    <table className="w-full text-sm text-left">
      <caption className="sr-only">OEM Partners</caption>
      <thead className="text-gray-400">
        <tr><th scope="col" className="py-1">OEM Partner</th><th scope="col" className="py-1">Legal name</th><th scope="col" className="py-1">Status</th>
          <th scope="col" className="py-1">Customers</th><th scope="col" className="py-1">Support</th><th scope="col" className="py-1"><span className="sr-only">Open</span></th></tr>
      </thead>
      <tbody>
        {partners.map((p) => (
          <tr key={p.id} className={`border-t border-gray-700 ${p.id === selected ? 'bg-blue-900/30' : ''}`}>
            <td className="py-1.5 font-medium">{p.name}</td>
            <td className="py-1.5 text-gray-300">{p.legal_name || '—'}</td>
            <td className="py-1.5"><StatusBadge status={p.status} /></td>
            <td className="py-1.5">{p.customer_count}</td>
            <td className="py-1.5 text-gray-300 break-all">{p.support_email || p.support_url || p.support_phone || '—'}</td>
            <td className="py-1.5 text-right">
              <button className={`${btn} bg-gray-700 hover:bg-gray-600`} onClick={() => onSelect(p.id)} aria-label={`Open ${p.name}`}>Open</button>
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

/** Create / edit form with labelled fields; the server's validation stays authoritative. */
export function OEMProfileForm({ value, onChange, errors }: { value: OEMProfile; onChange: (v: OEMProfile) => void; errors: Partial<Record<keyof OEMProfile, string>> }) {
  return (
    <div className="grid sm:grid-cols-2 gap-3">
      {OEM_PROFILE_FIELDS.map((f) => {
        const id = `oem-${f.key}`;
        return (
          <div key={f.key} className={f.key === 'name' || f.key === 'support_url' ? 'sm:col-span-2' : ''}>
            <label htmlFor={id} className="block text-xs text-gray-300 mb-1">{f.label}{f.required ? ' (required)' : ''}</label>
            <input id={id} type={f.kind} maxLength={f.max} value={value[f.key] ?? ''} className={input}
              aria-invalid={errors[f.key] ? true : undefined} aria-describedby={errors[f.key] ? `${id}-error` : undefined}
              placeholder={f.kind === 'url' ? 'https://' : undefined}
              onChange={(e) => onChange({ ...value, [f.key]: e.target.value })} />
            {errors[f.key] && <p id={`${id}-error`} className="text-xs text-red-300 mt-1">{errors[f.key]}</p>}
          </div>
        );
      })}
    </div>
  );
}

export function OEMOverviewPanel({ partner, onEdit, onToggle, onDelete }: { partner: OEMPartner; onEdit: () => void; onToggle: () => void; onDelete?: () => void }) {
  const row = (label: string, v: string) => (
    <div className="py-1.5 grid grid-cols-3 gap-3 border-t border-gray-700 first:border-t-0">
      <dt className="text-gray-400">{label}</dt><dd className="col-span-2 break-words">{v || <span className="text-gray-500">—</span>}</dd>
    </div>
  );
  return (
    <div className="space-y-3">
      <div className="flex flex-wrap items-center gap-3">
        <StatusBadge status={partner.status} />
        {partner.disabled_at && <span className="text-xs text-gray-400">since {formatDate(partner.disabled_at)}</span>}
        <div className="ml-auto flex gap-2">
          <button className={`${btn} bg-gray-700 hover:bg-gray-600`} onClick={onEdit}>Edit</button>
          <button className={`${btn} ${partner.status === 'active' ? 'bg-red-800 hover:bg-red-700' : 'bg-green-700 hover:bg-green-600'}`} onClick={onToggle}>
            {partner.status === 'active' ? 'Disable' : 'Enable'}
          </button>
          {onDelete && <button className={`${btn} bg-red-900 hover:bg-red-800`} onClick={onDelete}>Delete…</button>}
        </div>
      </div>
      <dl className="text-sm">
        {row('Legal name', partner.legal_name)}
        {row('Contact', [partner.contact_name, partner.contact_email, partner.contact_phone].filter(Boolean).join(' · '))}
        {row('Support e-mail', partner.support_email)}
        {row('Support phone', partner.support_phone)}
        {row('Support URL', partner.support_url)}
        {row('Created', formatDate(partner.created_at))}
      </dl>
      <p className="text-xs text-gray-500 font-mono">{partner.id}</p>
    </div>
  );
}

export function OEMCustomersPanel({ customers }: { customers: OEMCustomer[] }) {
  if (customers.length === 0) {
    return <p className="text-sm text-gray-400">No customers belong to this OEM Partner. Assign one from Settings → Customers.</p>;
  }
  return (
    <table className="w-full text-sm text-left">
      <caption className="sr-only">Customers of this OEM Partner</caption>
      <thead className="text-gray-400"><tr><th scope="col" className="py-1">Customer</th><th scope="col" className="py-1">Networks</th><th scope="col" className="py-1">Created</th></tr></thead>
      <tbody>
        {customers.map((c) => (
          <tr key={c.id} className="border-t border-gray-700">
            <td className="py-1.5"><Link className="text-blue-300 hover:underline" to={`/Settings/customers?customer=${encodeURIComponent(c.id)}`}>{c.name}</Link></td>
            <td className="py-1.5">{c.network_count}</td>
            <td className="py-1.5 text-gray-300">{formatDate(c.created_at)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}


export function AccountStateBadge({ account }: { account: ManagedAccountStatus }) {
  const tone = account.state === 'active' ? 'border-green-600 text-green-300' : account.state === 'disabled' || account.state === 'locked'
    ? 'border-gray-500 text-gray-300' : 'border-yellow-600 text-yellow-200';
  return (
    <span className={`inline-block text-xs px-2 py-0.5 rounded border ${tone}`}>
      {ACCOUNT_STATE_LABEL[account.state]}
      {account.state === 'activation_pending' && account.activation.expires_at && ` · until ${formatDate(account.activation.expires_at)}`}
      {account.disabled_reason === 'oem' && ' (OEM disabled)'}
    </span>
  );
}

export function OEMAdministratorsPanel({ partner, users, assignments, onCreateUser, onGrant, onRevoke, onProvision, onActivation, onPasswordReset, onMFAReset, onDeleteUser, busy }: {
  partner: OEMPartner; users: OEMUser[]; assignments: OEMRoleAssignment[]; busy?: boolean;
  onCreateUser: (name: string, email: string, admin: boolean) => void; onGrant: (userId: string) => void; onRevoke: (assignmentId: string) => void;
  onProvision: (user: OEMUser) => void; onActivation: (user: OEMUser) => void;
  onPasswordReset?: (user: OEMUser) => void; onMFAReset?: (user: OEMUser) => void;
  onDeleteUser?: (user: OEMUser) => void;
}) {
  const [username, setUsername] = useState('');
  const [email, setEmail] = useState('');
  const [admin, setAdmin] = useState(false);
  const disabled = partner.status !== 'active';
  const emailError = email ? loginEmailError(email) : undefined;
  const assignmentOf = (userId: string) => assignments.find((a) => a.user_id === userId && a.role === 'oem_admin');
  return (
    <div className="space-y-4">
      <p className="text-xs text-gray-300 bg-gray-900/60 border border-gray-700 rounded p-3">{IDENTITY_NOTE}</p>
      {users.length === 0 ? <p className="text-sm text-gray-400">This OEM Partner has no users.</p> : (
        <table className="w-full text-sm text-left">
          <caption className="sr-only">OEM users and administrators</caption>
          <thead className="text-gray-400">
            <tr><th scope="col" className="py-1">User</th><th scope="col" className="py-1">Login e-mail</th><th scope="col" className="py-1">Sign-in</th>
              <th scope="col" className="py-1">OEM administrator</th><th scope="col" className="py-1"><span className="sr-only">Actions</span></th></tr>
          </thead>
          <tbody>
            {users.map((u) => {
              const a = assignmentOf(u.id);
              const act = activationAction(u.account);
              const rec = recoveryActions(u.account);
              return (
                <tr key={u.id} className="border-t border-gray-700 align-top">
                  <td className="py-1.5">{u.username}{u.disabled && <span className="ml-2 text-xs text-gray-400">(disabled)</span>}
                    {u.status === 'invited' && <span className="ml-2 text-xs text-yellow-200">(invited)</span>}
                    <div className="text-xs text-gray-400 break-all">{u.email}</div></td>
                  <td className="py-1.5 text-gray-300 break-all">{u.account.login_email || <span className="text-gray-500">—</span>}</td>
                  <td className="py-1.5"><AccountStateBadge account={u.account} /></td>
                  <td className="py-1.5">{a ? 'Yes' : 'No'}</td>
                  <td className="py-1.5 text-right space-x-1 space-y-1">
                    {!u.account.provisioned && (
                      <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy || disabled || u.disabled} onClick={() => onProvision(u)}>Set up sign-in</button>
                    )}
                    {act && (
                      <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy || disabled} onClick={() => onActivation(u)}>
                        {act === 'issue' ? 'Issue activation link' : 'Reissue activation link'}
                      </button>
                    )}
                    {rec.passwordReset && onPasswordReset && (
                      <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onPasswordReset(u)}>Issue password reset</button>
                    )}
                    {rec.mfaReset && onMFAReset && (
                      <button className={`${btn} bg-red-800 hover:bg-red-700`} disabled={busy} onClick={() => onMFAReset(u)}>Reset MFA</button>
                    )}
                    {a ? <button className={`${btn} bg-gray-700 hover:bg-gray-600`} disabled={busy} onClick={() => onRevoke(a.id)}>Revoke administrator</button>
                      : <button className={`${btn} bg-blue-600 hover:bg-blue-500`} disabled={busy || disabled} onClick={() => onGrant(u.id)}>Grant administrator</button>}
                    {onDeleteUser && <button className={`${btn} bg-red-900 hover:bg-red-800`} disabled={busy} onClick={() => onDeleteUser(u)}>Remove from OEM</button>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
      <form className="grid sm:grid-cols-4 gap-2 items-end"
        onSubmit={(e) => { e.preventDefault(); onCreateUser(username.trim(), email.trim(), admin); setUsername(''); setEmail(''); setAdmin(false); }}>
        <div>
          <label htmlFor="oem-user-name" className="block text-xs text-gray-300 mb-1">Name</label>
          <input id="oem-user-name" className={input} value={username} maxLength={100} onChange={(e) => setUsername(e.target.value)} />
        </div>
        <div>
          <label htmlFor="oem-user-email" className="block text-xs text-gray-300 mb-1">E-mail address</label>
          <input id="oem-user-email" type="email" className={input} value={email} maxLength={254} autoComplete="off"
            aria-invalid={emailError ? true : undefined} aria-describedby={emailError ? 'oem-user-email-error' : undefined}
            onChange={(e) => setEmail(e.target.value)} />
          {emailError && <p id="oem-user-email-error" className="text-xs text-red-300 mt-1">{emailError}</p>}
        </div>
        <label className="flex items-center gap-2 text-sm pb-2">
          <input type="checkbox" checked={admin} onChange={(e) => setAdmin(e.target.checked)} /> OEM administrator
        </label>
        <button type="submit" className={`${btn} bg-blue-600 hover:bg-blue-500`}
          disabled={busy || disabled || !username.trim() || !email.trim() || !!emailError}>Invite person</button>
      </form>
      <p className="text-xs text-gray-400">The person is invited and accepts with Microsoft, Google or a RadioOS password; until then they have no access.</p>
      {disabled && <p className="text-xs text-yellow-200">This OEM Partner is disabled: no users, administrators or sign-in accounts can be added. Existing grants can be revoked.</p>}
    </div>
  );
}

/** Shows a freshly issued activation link exactly once; closing it discards the link. */
export function ActivationLinkDialog({ username, link, onClose, kind = 'activation' }: { username: string; link: ActivationLink; onClose: () => void; kind?: 'activation' | 'reset' }) {
  const [copied, setCopied] = useState(false);
  const what = kind === 'reset' ? 'password-reset link' : 'activation link';
  const url = link.activation_url;
  if (!url) {
    // E-mailed (or, with the link fallback off, not shown at all).
    return (
      <Dialog title={`${kind === 'reset' ? 'Password reset' : 'Activation'} for ${username}`} onCancel={onClose}>
        <p className={link.delivery_state === 'sent' ? 'text-gray-300' : 'font-semibold text-yellow-200'}>{link.notice}</p>
        {link.reissued && <p className="text-gray-300">The previous {what} no longer works.</p>}
        {kind === 'reset' && <p className="text-gray-300">The user keeps the authenticator app; it is still required at the next sign-in.</p>}
        <p className="text-xs text-gray-400">Valid until {formatDate(link.expires_at)}.</p>
      </Dialog>
    );
  }
  return (
    <Dialog title={`${kind === 'reset' ? 'Password-reset link' : 'Activation link'} for ${username}`} onCancel={onClose}>
      {link.delivery_state === 'failed' && <p className="text-yellow-200">{link.notice}</p>}
      <p className="font-semibold text-yellow-200">This {what} is shown once. It cannot be retrieved again.</p>
      {link.reissued && <p className="text-gray-300">The previous {what} no longer works.</p>}
      {kind === 'reset' && <p className="text-gray-300">The user keeps the authenticator app; it is still required at the next sign-in.</p>}
      <label htmlFor="activation-link" className="block text-xs text-gray-300">{kind === 'reset' ? 'Password-reset link' : 'Activation link'} (valid until {formatDate(link.expires_at)})</label>
      <input id="activation-link" readOnly className={`${input} font-mono text-xs`} value={url} onFocus={(e) => e.currentTarget.select()} />
      <button type="button" className={`${btn} bg-blue-600 hover:bg-blue-500`}
        onClick={() => { navigator.clipboard?.writeText(url).then(() => setCopied(true), () => setCopied(false)); }}>
        {copied ? 'Copied' : 'Copy link'}
      </button>
      <p className="text-gray-300">Give it to the user through a trusted channel. Anyone holding the link can set the account&apos;s password until it is used or expires.
        The user chooses the password; you will not see it.</p>
    </Dialog>
  );
}

/** Asks for the login e-mail of an existing user that has no sign-in account yet. */
export function ProvisionDialog({ username, busy, onCancel, onConfirm }: { username: string; busy?: boolean; onCancel: () => void; onConfirm: (loginEmail: string) => void }) {
  const [loginEmail, setLoginEmail] = useState('');
  const error = loginEmail ? loginEmailError(loginEmail) : undefined;
  return (
    <Dialog title={`Set up sign-in for ${username}`} onCancel={onCancel} confirm="Create sign-in account" busy={busy}
      onConfirm={() => { if (!loginEmailError(loginEmail)) onConfirm(loginEmail.trim()); }}>
      <label htmlFor="provision-login" className="block text-xs text-gray-300">Login e-mail</label>
      <input id="provision-login" type="email" className={input} value={loginEmail} maxLength={254} autoComplete="off"
        aria-invalid={error ? true : undefined} aria-describedby={error ? 'provision-login-error' : undefined} onChange={(e) => setLoginEmail(e.target.value)} />
      {error && <p id="provision-login-error" className="text-xs text-red-300">{error}</p>}
      <p className="text-gray-300">No password is set here. Issue an activation link afterwards; the user chooses the password.</p>
    </Dialog>
  );
}

type DetailTab = 'overview' | 'customers' | 'administrators';
const DETAIL_TABS: { id: DetailTab; label: string }[] = [
  { id: 'overview', label: 'Overview' }, { id: 'customers', label: 'Customers' }, { id: 'administrators', label: 'Administrators' },
];

export default function OEMPartnersManager() {
  const [partners, setPartners] = useState<OEMPartner[] | null>(null);
  const [selected, setSelected] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [creating, setCreating] = useState<OEMProfile | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof OEMProfile, string>>>({});
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      setPartners(await listOEMPartners());
    } catch (err) {
      setPartners([]);
      setError(errorText(err));
    }
  };
  useEffect(() => { load(); }, []);

  const submitCreate = async () => {
    if (!creating) return;
    const errors = validateOEMProfile(creating, true);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    setBusy(true);
    try {
      const { id } = await createOEMPartner(creating);
      setCreating(null);
      setNotice('OEM Partner created.');
      await load();
      setSelected(id);
    } catch (err) {
      setError(errorText(err));
    } finally {
      setBusy(false);
    }
  };

  const partner = partners?.find((p) => p.id === selected);
  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="flex-1 min-w-[16rem]">
          <h2 className="text-xl font-bold">OEM Partners</h2>
          <p className="text-sm text-gray-400">
            An OEM Partner is an optional delegated-administration layer above customers. Its administrators get read-only administrative
            visibility of the OEM&apos;s customers - never operational data. Customers without an OEM Partner are direct RadioOS customers.
          </p>
        </div>
        <button className={`${btn} bg-blue-600 hover:bg-blue-500`} onClick={() => { setCreating({ name: '' }); setFormErrors({}); }}>New OEM Partner</button>
      </div>
      {error && <Banner kind="error" onClose={() => setError(null)}>{error}</Banner>}
      {notice && <Banner kind="ok" onClose={() => setNotice(null)}>{notice}</Banner>}
      <div className="bg-gray-800 border border-gray-700 rounded p-4">
        {partners === null ? <p role="status" className="text-sm text-gray-400">Loading…</p> : <OEMPartnersTable partners={partners} selected={selected} onSelect={setSelected} />}
      </div>
      {partner && <OEMPartnerDetail key={partner.id} partner={partner} onChanged={load} onError={setError} onNotice={setNotice}
        onDeleted={(n) => { setSelected(''); setNotice(n); load(); }} />}
      {creating && (
        <Dialog title="New OEM Partner" onCancel={() => setCreating(null)} onConfirm={submitCreate} confirm="Create" busy={busy}>
          <OEMProfileForm value={creating} onChange={setCreating} errors={formErrors} />
        </Dialog>
      )}
    </div>
  );
}

function Banner({ kind, children, onClose }: { kind: 'error' | 'ok'; children: ReactNode; onClose: () => void }) {
  return (
    <div role={kind === 'error' ? 'alert' : 'status'}
      className={`p-3 rounded text-sm flex justify-between gap-3 border ${kind === 'error' ? 'bg-red-900/50 border-red-700 text-red-200' : 'bg-green-900/40 border-green-700 text-green-200'}`}>
      <span>{children}</span>
      <button onClick={onClose} aria-label="Dismiss">×</button>
    </div>
  );
}

function OEMPartnerDetail({ partner, onChanged, onError, onNotice, onDeleted }: {
  partner: OEMPartner; onChanged: () => Promise<void>; onError: (m: string) => void; onNotice: (m: string) => void;
  onDeleted: (notice: string) => void;
}) {
  const [tab, setTab] = useState<DetailTab>('overview');
  const [editing, setEditing] = useState<OEMProfile | null>(null);
  const [formErrors, setFormErrors] = useState<Partial<Record<keyof OEMProfile, string>>>({});
  const [toggling, setToggling] = useState(false);
  const [customers, setCustomers] = useState<OEMCustomer[] | null>(null);
  const [users, setUsers] = useState<OEMUser[] | null>(null);
  const [assignments, setAssignments] = useState<OEMRoleAssignment[]>([]);
  const [busy, setBusy] = useState(false);
  const [provisioning, setProvisioning] = useState<OEMUser | null>(null);
  const [activation, setActivation] = useState<{ username: string; link: ActivationLink; kind: 'activation' | 'reset' } | null>(null);
  const [issued, setIssued] = useState<IssuedInvitation | null>(null);
  const [mfaResetting, setMFAResetting] = useState<OEMUser | null>(null);
  const [deletingUser, setDeletingUser] = useState<OEMUser | null>(null);
  const [deletingOEM, setDeletingOEM] = useState<OEMDeleteImpact | null>(null);
  const openOEMDelete = async () => {
    setBusy(true);
    try {
      setDeletingOEM(await previewOEMPartnerDelete(partner.id));
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const confirmOEMDelete = async () => {
    setBusy(true);
    try {
      const res = await deleteOEMPartner(partner.id);
      setDeletingOEM(null);
      onDeleted(deletedNotice('oem', { oem: res.impact }));
    } catch (err) {
      setDeletingOEM(null);
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const profile: OEMProfile = Object.fromEntries(OEM_PROFILE_FIELDS.map((f) => [f.key, partner[f.key] ?? '']));

  const loadTab = async () => {
    try {
      if (tab === 'customers') setCustomers(await listOEMCustomers(partner.id));
      if (tab === 'administrators') {
        const [u, a] = await Promise.all([listOEMUsers(partner.id), listOEMRoleAssignments(partner.id)]);
        setUsers(u);
        setAssignments(a);
      }
    } catch (err) {
      onError(errorText(err));
    }
  };
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadTab(); }, [tab, partner.id]);

  const act = async (fn: () => Promise<unknown>, done: string) => {
    setBusy(true);
    try {
      await fn();
      onNotice(done);
      await onChanged();
      await loadTab();
    } catch (err) {
      onError(errorText(err));
    } finally {
      setBusy(false);
    }
  };
  const saveEdit = async () => {
    if (!editing) return;
    const changes = changedProfile(profile, editing);
    const errors = validateOEMProfile(changes, false);
    setFormErrors(errors);
    if (Object.keys(errors).length) return;
    if (Object.keys(changes).length === 0) { setEditing(null); return; }
    await act(() => updateOEMPartner(partner.id, changes), 'OEM Partner updated.');
    setEditing(null);
  };
  const status = describeOEMStatusChange(partner.name, partner.status !== 'active');

  return (
    <section aria-labelledby="oem-detail" className="bg-gray-800 border border-gray-700 rounded p-4 space-y-4">
      <h3 id="oem-detail" className="text-lg font-semibold">{partner.name}</h3>
      <nav aria-label="OEM Partner sections" className="flex gap-2 border-b border-gray-700">
        {DETAIL_TABS.map((t) => (
          <button key={t.id} onClick={() => setTab(t.id)} aria-current={tab === t.id ? 'page' : undefined}
            className={`px-4 py-2 -mb-px border-b-2 text-sm font-medium ${tab === t.id ? 'border-blue-500 text-white' : 'border-transparent text-gray-400 hover:text-gray-200'}`}>
            {t.label}
          </button>
        ))}
      </nav>
      {tab === 'overview' && <OEMOverviewPanel partner={partner} onEdit={() => { setEditing(profile); setFormErrors({}); }} onToggle={() => setToggling(true)} onDelete={openOEMDelete} />}
      {tab === 'customers' && (customers === null ? <p role="status" className="text-sm text-gray-400">Loading…</p> : <OEMCustomersPanel customers={customers} />)}
      {tab === 'administrators' && (users === null ? <p role="status" className="text-sm text-gray-400">Loading…</p> : (
        <OEMAdministratorsPanel partner={partner} users={users} assignments={assignments} busy={busy}
          onCreateUser={(u, e, admin) => act(async () => setIssued(await createOEMUser(partner.id, u, e, admin)), `${e} invited.`)}
          onGrant={(userId) => act(() => grantOEMAdmin(partner.id, userId), 'OEM administrator granted.')}
          onRevoke={(id) => act(() => revokeOEMAdmin(partner.id, id), 'OEM administrator revoked.')}
          onProvision={setProvisioning}
          onActivation={(u) => {
            // The link is held only in this component's state and discarded when the dialog closes.
            act(async () => setActivation({ username: u.username, link: await issueActivation(u.id), kind: 'activation' }),
              activationAction(u.account) === 'reissue' ? 'New activation link issued; the previous link no longer works.' : 'Activation link issued.');
          }}
          onPasswordReset={(u) => act(async () => setActivation({ username: u.username, link: await issuePasswordReset(u.id), kind: 'reset' }), 'Password reset issued.')}
          onMFAReset={setMFAResetting} onDeleteUser={setDeletingUser} />
      ))}
      {provisioning && (
        <ProvisionDialog username={provisioning.username} busy={busy} onCancel={() => setProvisioning(null)}
          onConfirm={async (loginEmail) => { await act(() => provisionManagedAccount(provisioning.id, loginEmail), 'Sign-in account created. Issue an activation link next.'); setProvisioning(null); }} />
      )}
      {activation && <ActivationLinkDialog username={activation.username} link={activation.link} kind={activation.kind} onClose={() => setActivation(null)} />}
      {issued && <IssuedInvitationDialog issued={issued} onClose={() => setIssued(null)} />}
      {mfaResetting && (() => {
        const d = describeMFAReset(mfaResetting.username, mfaResetting.account.login_email || '');
        return (
          <Dialog title={d.title} onCancel={() => setMFAResetting(null)} confirm={d.confirm} busy={busy} danger
            onConfirm={async () => { await act(() => resetMFA(mfaResetting.id), `MFA reset for ${mfaResetting.username}: authenticator and recovery codes removed, sessions ended.`); setMFAResetting(null); }}>
            <ul className="list-disc pl-5 space-y-1">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>
          </Dialog>
        );
      })()}
      {deletingUser && (() => {
        const d = describeOEMUserDelete(deletingUser);
        return (
          <Dialog title={d.title} onCancel={() => setDeletingUser(null)} confirm={d.confirm} busy={busy} danger
            onConfirm={async () => {
              await act(async () => { await deleteOEMUser(partner.id, deletingUser.id); }, `${deletingUser.username} removed from ${partner.name}.`);
              setDeletingUser(null);
            }}>
            <ul className="list-disc pl-5 space-y-1">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>
          </Dialog>
        );
      })()}
      {deletingOEM && (() => {
        const d = describeOEMDelete(deletingOEM);
        return (
          <Dialog title={d.title} onCancel={() => setDeletingOEM(null)} confirm={d.confirm} busy={busy} danger onConfirm={confirmOEMDelete}>
            <ul className="list-disc pl-5 space-y-1">{d.points.map((p) => <li key={p}>{p}</li>)}</ul>
          </Dialog>
        );
      })()}
      {editing && (
        <Dialog title={`Edit ${partner.name}`} onCancel={() => setEditing(null)} onConfirm={saveEdit} confirm="Save" busy={busy}>
          <OEMProfileForm value={editing} onChange={setEditing} errors={formErrors} />
        </Dialog>
      )}
      {toggling && (
        <Dialog title={status.title} onCancel={() => setToggling(false)} confirm={status.confirm} busy={busy} danger={partner.status === 'active'}
          onConfirm={async () => { await act(() => setOEMPartnerEnabled(partner.id, partner.status !== 'active'), `OEM Partner ${partner.status === 'active' ? 'disabled' : 'enabled'}.`); setToggling(false); }}>
          <ul className="list-disc pl-5 space-y-1">{status.points.map((p) => <li key={p}>{p}</li>)}</ul>
        </Dialog>
      )}
    </section>
  );
}
